import { useEffect, useState } from "react";
import { MessageCircle, Phone, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LeadForm } from "@/components/landing/LeadForm";
import { CONTACT } from "@/data/siteData";

const WA_LINK = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GhumneChaley! We want to plan a group trip.")}`;

export function WhatsAppFloat() {
    const [dismissed, setDismissed] = useState(false); // user closed it via X — stays hidden
    const [atFooter, setAtFooter] = useState(false);   // auto-hidden only while footer is visible

    // Hide the tooltip while the footer is in view — it otherwise overlaps and
    // blocks clicks on footer links (e.g. Admin Login) — and bring it back
    // when the visitor scrolls back up into the page.
    useEffect(() => {
        const footer = document.querySelector("footer, [data-testid='site-footer']");
        if (!footer) return;
        const obs = new IntersectionObserver(([entry]) => setAtFooter(entry.isIntersecting), { threshold: 0.05 });
        obs.observe(footer);
        return () => obs.disconnect();
    }, []);

    const bubble = !dismissed && !atFooter;

    return (
        <div className="fixed bottom-24 md:bottom-6 right-4 z-40 flex flex-col items-end gap-3" data-testid="suitcase-chat">
            {bubble && (
                <div className="chat-bubble-in relative bg-white rounded-2xl border border-stone-300 shadow-xl px-4 py-3 max-w-[210px]" data-testid="chat-bubble">
                    <button
                        onClick={() => setDismissed(true)}
                        className="absolute -top-2 -left-2 h-5 w-5 rounded-full bg-navy text-white flex items-center justify-center"
                        aria-label="Dismiss"
                        data-testid="chat-bubble-close"
                    >
                        <X className="h-3 w-3" />
                    </button>
                    <p className="font-script text-2xl text-navy leading-none">Kahan chale?</p>
                    <p className="text-[11px] text-stone-500 mt-1 leading-snug">Trip plan chahiye? WhatsApp pe bolo — reply in minutes!</p>
                    <span className="absolute -bottom-[7px] right-7 h-3.5 w-3.5 bg-white border-b border-r border-stone-300 rotate-45" />
                </div>
            )}
            <a
                href={WA_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-float-btn"
                aria-label="Chat with GhumneChaley on WhatsApp"
                className="group relative h-16 w-16 rounded-full bg-white border-2 border-saffron shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
            >
                <img src="/logo-suitcase-icon.png" alt="" className="suitcase-float h-10 w-auto" />
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#25D366] border-2 border-white flex items-center justify-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                </span>
            </a>
        </div>
    );
}

export function MobileStickyCTA() {
    return (
        <div className="fixed bottom-0 inset-x-0 z-40 md:hidden grid grid-cols-3 border-t border-stone-300 bg-white" data-testid="mobile-sticky-cta">
            <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} data-testid="mobile-cta-call" className="flex items-center justify-center gap-1.5 py-3.5 text-xs font-display font-black uppercase tracking-wider text-navy">
                <Phone className="h-4 w-4" /> Call
            </a>
            <a href={WA_LINK} target="_blank" rel="noreferrer" data-testid="mobile-cta-whatsapp" className="flex items-center justify-center gap-1.5 py-3.5 text-xs font-display font-black uppercase tracking-wider text-white bg-[#25D366]">
                <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <button onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry", { detail: {} }))} data-testid="mobile-cta-plan" className="py-3.5 text-xs font-display font-black uppercase tracking-wider text-white bg-saffron">
                Plan My Trip
            </button>
        </div>
    );
}

export function ConversionWidgets() {
    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [exitOpen, setExitOpen] = useState(false);

    useEffect(() => {
        const open = () => setEnquiryOpen(true);
        window.addEventListener("open-enquiry", open);
        return () => window.removeEventListener("open-enquiry", open);
    }, []);

    // Exit intent — desktop, once per session
    useEffect(() => {
        const onMouseOut = (e) => {
            if (e.relatedTarget || e.clientY > 8) return;
            if (sessionStorage.getItem("gc_exit_shown")) return;
            sessionStorage.setItem("gc_exit_shown", "1");
            setExitOpen(true);
        };
        document.addEventListener("mouseout", onMouseOut);
        return () => document.removeEventListener("mouseout", onMouseOut);
    }, []);

    return (
        <>
            <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
                <DialogContent className="max-w-md bg-cream border-stone-300" data-testid="enquiry-popup">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl text-navy">Plan My Trip</DialogTitle>
                        <DialogDescription className="text-sm text-stone-500">
                            Tell us the basics — a Trip Expert calls you back with a custom plan.
                        </DialogDescription>
                    </DialogHeader>
                    <LeadForm idPrefix="popup" source="popup" buttonLabel="Plan My Trip" onSuccess={() => setTimeout(() => setEnquiryOpen(false), 2200)} />
                </DialogContent>
            </Dialog>

            <Dialog open={exitOpen} onOpenChange={setExitOpen}>
                <DialogContent className="max-w-md bg-navy text-white border-navy" data-testid="exit-intent-popup">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-3xl leading-tight">
                            Before You Go…<br /><span className="text-gold">Where Are You GhumneChaley-ing?</span>
                        </DialogTitle>
                        <DialogDescription className="text-white/60 text-sm">
                            One form. One call back. One unforgettable trip.
                        </DialogDescription>
                    </DialogHeader>
                    <LeadForm idPrefix="exit" source="exit-intent" dark buttonLabel="Plan My Trip" onSuccess={() => setTimeout(() => setExitOpen(false), 2200)} />
                </DialogContent>
            </Dialog>
        </>
    );
}
